'use client';

import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as React from 'react';

import { cn } from '../utils/cn';

/**
 * `segmented` — muted track with a raised active thumb (app default).
 * `underline` — full-width row with a 2px brand underline on the active tab
 * (admin detail pages).
 */
export type TabsVariant = 'segmented' | 'underline';

const TabsVariantContext = React.createContext<TabsVariant>('segmented');

const tabsListVariantClass: Record<TabsVariant, string> = {
  segmented:
    'inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground',
  underline:
    'relative flex w-full items-end gap-0.5 overflow-x-auto border-b border-[color:var(--surface-border)] text-muted-foreground [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
};

const tabsTriggerVariantClass: Record<TabsVariant, string> = {
  segmented:
    'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
  underline:
    '-mb-px inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 border-transparent px-3.5 py-2.5 text-[13.5px] font-semibold transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 data-[state=active]:border-primary data-[state=active]:text-foreground',
};

const Tabs = TabsPrimitive.Root;

type TabsListProps = React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.List
> & {
  /** Visual style for this list and its triggers. Defaults to `segmented`. */
  variant?: TabsVariant;
};

/**
 * Underline lists scroll sideways when the tabs don't fit: fade the edge that
 * hides more tabs, and keep the active tab in view (deep links, programmatic
 * switches).
 */
function useOverflowHint(
  ref: React.RefObject<HTMLDivElement | null>,
  enabled: boolean,
) {
  const [edges, setEdges] = React.useState({ start: false, end: false });
  React.useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return;
    const update = () =>
      setEdges({
        start: el.scrollLeft > 1,
        end: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
      });
    const reveal = () => {
      const active = el.querySelector<HTMLElement>('[data-state="active"]');
      if (!active) return;
      const left = active.offsetLeft;
      const right = left + active.offsetWidth;
      if (left < el.scrollLeft) el.scrollLeft = Math.max(0, left - 24);
      else if (right > el.scrollLeft + el.clientWidth)
        el.scrollLeft = right - el.clientWidth + 24;
    };
    reveal();
    update();
    el.addEventListener('scroll', update, { passive: true });
    const resize = new ResizeObserver(update);
    resize.observe(el);
    const mutation = new MutationObserver(() => {
      reveal();
      update();
    });
    mutation.observe(el, {
      subtree: true,
      attributes: true,
      attributeFilter: ['data-state'],
    });
    return () => {
      el.removeEventListener('scroll', update);
      resize.disconnect();
      mutation.disconnect();
    };
  }, [ref, enabled]);
  return edges;
}

function edgeMask(edges: { start: boolean; end: boolean }) {
  if (!edges.start && !edges.end) return undefined;
  const mask = `linear-gradient(to right, ${edges.start ? 'transparent, #000 32px' : '#000'}, ${edges.end ? '#000 calc(100% - 32px), transparent' : '#000'})`;
  return { maskImage: mask, WebkitMaskImage: mask };
}

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, variant = 'segmented', style, ...props }, ref) => {
  const innerRef = React.useRef<HTMLDivElement | null>(null);
  React.useImperativeHandle(ref, () => innerRef.current as HTMLDivElement);
  const edges = useOverflowHint(innerRef, variant === 'underline');
  return (
    <TabsVariantContext.Provider value={variant}>
      <TabsPrimitive.List
        ref={innerRef}
        data-variant={variant}
        className={cn(tabsListVariantClass[variant], className)}
        style={{ ...edgeMask(edges), ...style }}
        {...props}
      />
    </TabsVariantContext.Provider>
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

type TabsTriggerProps = React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.Trigger
> & {
  /** Optional count pill after the label (e.g. number of orders). Ignored with `asChild`. */
  count?: number | string | null;
};

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, count, children, ...props }, ref) => {
  const variant = React.useContext(TabsVariantContext);
  // Slot (`asChild`) needs a single child, so the pill is skipped there.
  const showCount = !props.asChild && count != null && count !== '';
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(tabsTriggerVariantClass[variant], className)}
      {...props}
    >
      {children}
      {showCount ? (
        <span className="rounded-full bg-muted px-[7px] text-[11.5px] font-medium tabular-nums text-muted-foreground">
          {count}
        </span>
      ) : null}
    </TabsPrimitive.Trigger>
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      // Hide scrollbar but keep scrolling functionality
      '[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger };
export type { TabsListProps, TabsTriggerProps };
